import { computed } from 'vue'
import enums from './enums'

export default {
  /**
   *
   * @param {*} app
   * @param {*} options { i18n }
   */
  install: (app, options) => {
    const { t } = options.i18n.global

    // en-BE does some rather strange things with formatting: using neutral 'en-GB' instead:
    const locale = computed(() => {
      const _locale = options.i18n.global.locale.value

      return _locale.startsWith('en') || _locale === 'technical' ? 'en-GB' : _locale
    })

    const formatDate = (date, { fn, options } = {}) => {
      if (!date) {
        return t('monolith.global.na')
      }

      const dateObj = new Date(date)
      if (isNaN(dateObj)) {
        return t('monolith.global.na')
      }

      return typeof fn === 'function' ? fn(dateObj) : dateObj.toLocaleDateString(locale.value, options)
    }

    // inject a globally available $format object
    app.config.globalProperties.$format = {
      date: (date, options = {}) => {
        return formatDate(date, { options })
      },

      dateLong: (date, fmtOptions = { weekday: true }) => {
        return formatDate(date, {
          options: {
            weekday: fmtOptions.weekday ? 'long' : undefined,
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          },
        })
      },

      dateShort: (date) => {
        return formatDate(date, {
          options: {
            year: 'numeric',
            month: 'numeric',
          },
        })
      },

      datetime: (date) => {
        return formatDate(date, {
          options: {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
          },
        })
      },

      datetimeLong: (date) => {
        return formatDate(date, {
          options: {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
          },
        })
      },

      time: (date) => {
        const fn = (dateObj) =>
          dateObj.toLocaleString(locale.value, {
            hour: 'numeric',
            minute: 'numeric',
          })

        return formatDate(date, { fn })
      },

      currency: (number, { showNa = true, showCents = false, ...fmtOptions } = {}) => {
        if ((number === undefined || number === null) && showNa) {
          return t('monolith.global.na')
        }

        if (fmtOptions.nearest === true) {
          if (number >= 10000) fmtOptions.nearest = 1000
          else if (number > 1000) fmtOptions.nearest = 10
        }
        if (fmtOptions.nearest === undefined) {
          fmtOptions.nearest = 1
        }

        return new Intl.NumberFormat(locale.value, {
          style: 'currency',
          currency: 'EUR',
          notation: fmtOptions.compact ? 'compact' : 'standard',
          maximumFractionDigits: fmtOptions.compact ? 1 : fmtOptions.nearest ? 0 : showCents,
          ...fmtOptions,
        }).format(fmtOptions.nearest ? Math.round(number / fmtOptions.nearest) * fmtOptions.nearest : number)
      },

      number: (number, fmtOptions = {}) => {
        return new Intl.NumberFormat(locale.value, {
          style: 'decimal',
          maximumFractionDigits: 2,
          ...fmtOptions,
        }).format(number)
      },

      integer: (number, fmtOptions = {}) => {
        return new Intl.NumberFormat(locale.value, {
          style: 'decimal',
          maximumFractionDigits: 0,
          ...fmtOptions,
        }).format(number)
      },

      round: (number, fmtOptions = { nearest: 1000 }) => {
        return new Intl.NumberFormat(locale.value, {
          style: 'decimal',
          maximumFractionDigits: 0,
          ...fmtOptions,
        }).format(Math.round(number / fmtOptions.nearest) * fmtOptions.nearest)
      },

      appointment: (apt) => {
        if (apt?.status === enums.appointmentStatus.INCOMPLETE) {
          return t('monolith.global.tbd')
        }

        if (!apt || !apt?.start_time) {
          return t('monolith.global.na')
        }

        return `${app.config.globalProperties.$format.date(apt?.start_time)} - ${app.config.globalProperties.$format.time(apt?.start_time)}`
      },
    }
  },
}
